"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  Fragment,
  isValidElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  CSSProperties,
  ElementType,
  ReactElement,
  ReactNode,
} from "react";

import { cn } from "@/lib/utils";

type ScrollRevealTextVariant = "light" | "dark";
type ScrollRevealMode = "character" | "word" | "sentence" | "whole";

type ScrollRevealTextProps = {
  children: ReactNode;
  className?: string;
  variant?: ScrollRevealTextVariant;
  as?: ElementType;
  /** Per-character, per-word, per-sentence line reveal, or `whole` block (default: word). */
  mode?: ScrollRevealMode;
};

/** Design tokens — grey-400 → grey-950 (light), white/40 → white (dark). */
const VARIANT_COLORS: Record<
  ScrollRevealTextVariant,
  { muted: string; primary: string }
> = {
  light: {
    muted: "#95999D",
    primary: "#0C0D0D",
  },
  dark: {
    muted: "rgba(255, 255, 255, 0.4)",
    primary: "rgb(255, 255, 255)",
  },
};

/** Strong ease-out — fast start, soft settle (design-eng / Framer Studio Terrace). */
const REVEAL_EASE = [0.16, 1, 0.3, 1] as const;
/** Per-line reveal — most travel happens in the first ~40% of the curve. */
const REVEAL_DURATION = 0.5;
/** Delay between each visual line (Framer Studio Terrace pattern). */
const STAGGER_PER_LINE = 0.12;

const HIDDEN_TRANSFORM = "translate3d(0, 100%, 0)";
const VISIBLE_TRANSFORM = "translate3d(0, 0, 0)";
/** Room below the line box so overflow-hidden masks don't crop descenders (g, y, p). */
const LINE_CLIP_DESCENDER_PADDING = "0.14em";

const lineContainerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: STAGGER_PER_LINE,
    },
  },
};

type Segment =
  | { kind: "text"; text: string }
  | {
      kind: "accent";
      text: string;
      className?: string;
      style?: CSSProperties;
    }
  | { kind: "break" }
  | { kind: "persistentMuted"; text: string; className?: string };

type RenderPiece =
  | { kind: "break" }
  | { kind: "space"; text: string }
  | { kind: "reveal"; text: string }
  | { kind: "persistentMuted"; text: string; className?: string }
  | {
      kind: "accent";
      text: string;
      className?: string;
      style?: CSSProperties;
    };

const SCROLL_REVEAL_ACCENT_ATTR = "data-scroll-reveal-accent";
const SCROLL_REVEAL_MUTED_ATTR = "data-scroll-reveal-muted";

type ScrollRevealAccentComponent = {
  (props: {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
  }): ReactElement;
  scrollRevealAccentMarker: true;
};

/** Gradient words inside `ScrollRevealText` — grey-400 → gradient (light variant). */
export function ScrollRevealAccent({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span data-scroll-reveal-accent className={className} style={style}>
      {children}
    </span>
  );
}
ScrollRevealAccent.scrollRevealAccentMarker = true;

type ScrollRevealMutedComponent = {
  (props: { children: ReactNode; className?: string }): ReactElement;
  scrollRevealMutedMarker: true;
};

/** Stays muted inside `ScrollRevealText` while lead copy animates to primary. */
export function ScrollRevealMuted({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span data-scroll-reveal-muted className={className}>
      {children}
    </span>
  );
}
ScrollRevealMuted.scrollRevealMutedMarker = true;

function isScrollRevealMutedElement(node: ReactElement): node is ReactElement<{
  children?: ReactNode;
  className?: string;
  [SCROLL_REVEAL_MUTED_ATTR]?: boolean;
}> {
  if (
    (node.type as ScrollRevealMutedComponent).scrollRevealMutedMarker === true
  ) {
    return true;
  }

  const props = node.props as Record<string, unknown>;
  return props[SCROLL_REVEAL_MUTED_ATTR] !== undefined;
}

function isScrollRevealAccentElement(node: ReactElement): node is ReactElement<{
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  [SCROLL_REVEAL_ACCENT_ATTR]?: boolean;
}> {
  if (
    (node.type as ScrollRevealAccentComponent).scrollRevealAccentMarker === true
  ) {
    return true;
  }

  const props = node.props as Record<string, unknown>;
  return props[SCROLL_REVEAL_ACCENT_ATTR] !== undefined;
}

function textFromNode(node: ReactNode): string {
  let result = "";

  function walk(value: ReactNode) {
    if (value == null || value === false) return;
    if (typeof value === "string" || typeof value === "number") {
      result += String(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (isValidElement(value)) {
      // An explicit break separates words in the accessible name.
      if (value.type === "br") {
        result += " ";
        return;
      }
      walk((value as ReactElement<{ children?: ReactNode }>).props.children);
    }
  }

  walk(node);
  return result;
}

function isMeasurablePiece(piece: RenderPiece): piece is
  | { kind: "reveal"; text: string }
  | { kind: "persistentMuted"; text: string; className?: string }
  | {
      kind: "accent";
      text: string;
      className?: string;
      style?: CSSProperties;
    } {
  return (
    piece.kind === "reveal" ||
    piece.kind === "persistentMuted" ||
    piece.kind === "accent"
  );
}

function segmentsFromChildren(children: ReactNode): Segment[] {
  const segments: Segment[] = [];

  function walk(node: ReactNode) {
    if (node == null || node === false) return;
    if (typeof node === "string" || typeof node === "number") {
      segments.push({ kind: "text", text: String(node) });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (isValidElement(node)) {
      if (node.type === "br") {
        segments.push({ kind: "break" });
        return;
      }

      if (isScrollRevealAccentElement(node)) {
        segments.push({
          kind: "accent",
          text: textFromNode(node.props.children),
          className: node.props.className,
          style: node.props.style,
        });
        return;
      }

      if (isScrollRevealMutedElement(node)) {
        segments.push({
          kind: "persistentMuted",
          text: textFromNode(node.props.children),
          className: node.props.className,
        });
        return;
      }

      walk((node as ReactElement<{ children?: ReactNode }>).props.children);
    }
  }

  walk(children);
  return segments;
}

function splitText(text: string, mode: ScrollRevealMode): string[] {
  if (mode === "character") return text.split("");
  if (mode === "word") return text.match(/([\S]+|\s+)/g) ?? [];
  return text.match(/[^.!?\n]+(?:[.!?]+)?|\n|\s+/g) ?? [];
}

function piecesFromSegments(
  segments: Segment[],
  mode: ScrollRevealMode
): RenderPiece[] {
  const pieces: RenderPiece[] = [];

  for (const segment of segments) {
    if (segment.kind === "break") {
      pieces.push({ kind: "break" });
      continue;
    }

    const accentMeta =
      segment.kind === "accent"
        ? { className: segment.className, style: segment.style }
        : null;
    const mutedMeta =
      segment.kind === "persistentMuted"
        ? { className: segment.className }
        : null;

    for (const item of splitText(segment.text, mode)) {
      if (item === "\n") {
        pieces.push({ kind: "break" });
        continue;
      }
      if (item.trim().length === 0) {
        pieces.push({ kind: "space", text: item });
        continue;
      }
      if (accentMeta) {
        pieces.push({ kind: "accent", text: item, ...accentMeta });
        continue;
      }
      if (mutedMeta) {
        pieces.push({ kind: "persistentMuted", text: item, ...mutedMeta });
        continue;
      }
      pieces.push({ kind: "reveal", text: item });
    }
  }

  return pieces;
}

/** Split on explicit breaks, then sub-split each chunk by measured visual line. */
function piecesToLineGroups(
  pieces: RenderPiece[],
  wordTops: number[]
): RenderPiece[][] {
  const chunks: RenderPiece[][] = [[]];
  let wordIndex = 0;

  for (const piece of pieces) {
    if (piece.kind === "break") {
      if (chunks[chunks.length - 1]!.length > 0) {
        chunks.push([]);
      }
      continue;
    }

    chunks[chunks.length - 1]!.push(piece);

    if (isMeasurablePiece(piece)) {
      wordIndex++;
    }
  }

  const nonEmptyChunks = chunks.filter((chunk) => chunk.length > 0);
  const lines: RenderPiece[][] = [];
  let revealOffset = 0;

  for (const chunk of nonEmptyChunks) {
    const chunkRevealCount = chunk.filter(isMeasurablePiece).length;
    const chunkTops = wordTops.slice(
      revealOffset,
      revealOffset + chunkRevealCount
    );
    revealOffset += chunkRevealCount;

    if (chunkTops.length === 0) {
      lines.push(chunk);
      continue;
    }

    const uniqueTops = [...new Set(chunkTops)].toSorted((a, b) => a - b);
    const topToLine = new Map(uniqueTops.map((top, index) => [top, index]));
    const chunkLines: RenderPiece[][] = uniqueTops.map(() => []);

    let chunkWordIndex = 0;
    let pendingSpaces: RenderPiece[] = [];

    for (const piece of chunk) {
      if (piece.kind === "space") {
        pendingSpaces.push(piece);
        continue;
      }

      if (isMeasurablePiece(piece)) {
        const lineIndex = topToLine.get(chunkTops[chunkWordIndex] ?? 0) ?? 0;
        for (const space of pendingSpaces) {
          chunkLines[lineIndex]!.push(space);
        }
        pendingSpaces = [];
        chunkLines[lineIndex]!.push(piece);
        chunkWordIndex++;
      }
    }

    if (pendingSpaces.length > 0) {
      const lastLine =
        chunkLines.findLast((line) => line.length > 0) ?? chunkLines[0];
      lastLine?.push(...pendingSpaces);
    }

    for (const chunkLine of chunkLines) {
      if (chunkLine.length > 0) {
        lines.push(chunkLine);
      }
    }
  }

  return lines;
}

function measureWordTops(
  container: HTMLElement,
  wordElements: (HTMLSpanElement | null)[]
): number[] {
  const containerTop = container.getBoundingClientRect().top;

  return wordElements.map((element) => {
    if (!element) return 0;
    return Math.round(element.getBoundingClientRect().top - containerTop);
  });
}

function accentWordColorVariants(muted: string) {
  const transition = { duration: REVEAL_DURATION, ease: REVEAL_EASE };
  return {
    greyLayer: {
      hidden: { color: muted, opacity: 1 },
      show: { color: muted, opacity: 0, transition },
    },
    gradientLayer: {
      hidden: { opacity: 0 },
      show: { opacity: 1, transition },
    },
  };
}

function accentPieceClassName(className?: string) {
  return cn(
    "bg-clip-text [-webkit-background-clip:text]",
    className
      ?.replace(/\btext-transparent\b/g, "")
      .replace(/\[-webkit-text-fill-color:transparent\]/g, "")
      .trim()
  );
}

function accentPieceStyle(style?: CSSProperties): CSSProperties {
  return {
    ...style,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
  };
}

function accentFinalStyle(
  className?: string,
  style?: CSSProperties
): { className: string; style: CSSProperties } {
  return {
    className: cn(accentPieceClassName(className), "text-transparent"),
    style: {
      ...accentPieceStyle(style),
      WebkitTextFillColor: "transparent",
    },
  };
}

function renderAnimatedLinePieces(
  pieces: RenderPiece[],
  keyPrefix: string,
  wordColorVariants: {
    hidden: { color: string };
    show: {
      color: string;
      transition: { duration: number; ease: readonly number[] };
    };
  },
  accentGreyLayerVariants: {
    hidden: { color: string; opacity: number };
    show: {
      color: string;
      opacity: number;
      transition: { duration: number; ease: readonly number[] };
    };
  },
  accentGradientLayerVariants: {
    hidden: { opacity: number };
    show: {
      opacity: number;
      transition: { duration: number; ease: readonly number[] };
    };
  },
  persistentMutedColor: string
) {
  return pieces.map((piece, index) => {
    if (piece.kind === "break") {
      return <br key={`${keyPrefix}-break-${index}`} />;
    }
    if (piece.kind === "space") {
      return (
        <Fragment key={`${keyPrefix}-space-${index}`}>{piece.text}</Fragment>
      );
    }
    if (piece.kind === "persistentMuted") {
      return (
        <span
          key={`${keyPrefix}-muted-${index}`}
          className={piece.className}
          style={piece.className ? undefined : { color: persistentMutedColor }}
        >
          {piece.text}
        </span>
      );
    }
    if (piece.kind === "accent") {
      const accent = accentFinalStyle(piece.className, piece.style);
      return (
        <span
          key={`${keyPrefix}-accent-${index}`}
          className="relative inline-block"
        >
          <motion.span variants={accentGreyLayerVariants}>
            {piece.text}
          </motion.span>
          <motion.span
            aria-hidden
            className={cn(
              accent.className,
              "pointer-events-none absolute inset-0"
            )}
            style={accent.style}
            variants={accentGradientLayerVariants}
          >
            {piece.text}
          </motion.span>
        </span>
      );
    }
    return (
      <motion.span
        key={`${keyPrefix}-word-${index}`}
        variants={wordColorVariants}
      >
        {piece.text}
      </motion.span>
    );
  });
}

function renderMeasurePieces(
  pieces: RenderPiece[],
  wordRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>
) {
  let wordIndex = 0;

  return pieces.map((piece, index) => {
    if (piece.kind === "break") {
      return <br key={`measure-break-${index}`} />;
    }
    if (piece.kind === "space") {
      return <Fragment key={`measure-space-${index}`}>{piece.text}</Fragment>;
    }

    const currentWordIndex = wordIndex;
    wordIndex++;

    return (
      <span
        key={`measure-word-${index}`}
        ref={(element) => {
          wordRefs.current[currentWordIndex] = element;
        }}
        className={
          piece.kind === "accent"
            ? accentFinalStyle(piece.className, piece.style).className
            : piece.kind === "persistentMuted"
              ? piece.className
              : undefined
        }
        style={
          piece.kind === "accent"
            ? accentFinalStyle(piece.className, piece.style).style
            : undefined
        }
      >
        {piece.text}
      </span>
    );
  });
}

const MOTION_TAGS = {
  span: motion.span,
  p: motion.p,
  h1: motion.h1,
  blockquote: motion.blockquote,
} as const;

/**
 * Load-triggered text reveal: muted copy fades up line-by-line on first
 * appearance (all words on a line animate together, then the next line).
 * Plays once — does not replay on scroll.
 *
 * Renders the text content once. Earlier versions kept a second hidden copy
 * for layout reservation, which made extractors read every H1 twice.
 */
export function ScrollRevealText({
  children,
  className,
  variant = "light",
  as: Component = "span",
  mode = "word",
}: ScrollRevealTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduceMotion = useReducedMotion();
  const colors = VARIANT_COLORS[variant];
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const MotionTag =
    MOTION_TAGS[Component as keyof typeof MOTION_TAGS] ?? motion.span;

  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [lineGroups, setLineGroups] = useState<RenderPiece[][] | null>(null);
  const [measuredKey, setMeasuredKey] = useState<string | null>(null);
  const { pieces, label, revealCount, measureKey } = useMemo(() => {
    if (mode === "whole") {
      return {
        pieces: [] as RenderPiece[],
        label: textFromNode(children).replace(/\s+/g, " ").trim(),
        revealCount: 0,
        measureKey: "whole",
      };
    }
    const segments = segmentsFromChildren(children);
    const nextPieces = piecesFromSegments(segments, mode);
    return {
      pieces: nextPieces,
      // Explicit breaks read as a space, so `{line1}<br />{line2}` is not
      // announced as one run-on word; whitespace is then collapsed.
      label: segments
        .map((segment) => (segment.kind === "break" ? " " : segment.text))
        .join("")
        .replace(/\s+/g, " ")
        .trim(),
      revealCount: nextPieces.filter(isMeasurablePiece).length,
      // Keyed on the piece structure, not the label: `a<br />b` and `a b`
      // share a label but need different line groups, so a break must
      // invalidate the measurement (responsive hero titles swap between them).
      measureKey: nextPieces
        .map((piece) =>
          piece.kind === "break"
            ? "\n"
            : piece.kind === "space"
              ? " "
              : `${piece.kind}:${piece.text}`
        )
        .join("|")
        .concat(`|${mode}`),
    };
  }, [children, mode]);
  const isMeasured = measuredKey === measureKey && lineGroups !== null;

  useLayoutEffect(() => {
    if (mode === "whole") return;
    if (isMeasured) return;

    const container = ref.current;
    if (!container) return;

    wordRefs.current = wordRefs.current.slice(0, revealCount);

    const tops = measureWordTops(container, wordRefs.current);
    if (tops.length === 0) return;

    setLineGroups(piecesToLineGroups(pieces, tops));
    setMeasuredKey(measureKey);
  }, [isMeasured, measureKey, mode, pieces, revealCount]);

  useLayoutEffect(() => {
    if (mode === "whole") return;

    const container = ref.current;
    if (!container) return;

    const invalidate = () => {
      setLineGroups(null);
      setMeasuredKey(null);
    };

    const ro = new ResizeObserver(invalidate);
    ro.observe(container);

    void document.fonts?.ready.then(invalidate);

    return () => ro.disconnect();
  }, [mode]);

  if (mode === "whole") {
    if (reduceMotion) {
      return (
        <MotionTag
          ref={ref as never}
          className={className}
          style={{ color: colors.primary }}
        >
          {children}
        </MotionTag>
      );
    }

    const wholeBlockVariants = {
      hidden: {
        transform: HIDDEN_TRANSFORM,
        opacity: 0,
        color: colors.muted,
      },
      show: {
        transform: VISIBLE_TRANSFORM,
        opacity: 1,
        color: colors.primary,
        transition: {
          transform: { duration: REVEAL_DURATION, ease: REVEAL_EASE },
          opacity: { duration: REVEAL_DURATION * 0.75, ease: REVEAL_EASE },
          color: { duration: REVEAL_DURATION, ease: REVEAL_EASE },
        },
      },
    };

    return (
      <MotionTag ref={ref as never} className={className} aria-label={label}>
        <span className="block overflow-hidden">
          <motion.span
            className="block"
            initial="hidden"
            animate={isInView ? "show" : "hidden"}
            variants={wholeBlockVariants}
          >
            {children}
          </motion.span>
        </span>
      </MotionTag>
    );
  }

  if (reduceMotion) {
    return (
      <MotionTag
        ref={ref as never}
        className={className}
        style={{ color: colors.primary }}
        role="region"
        aria-label={label}
      >
        {children}
      </MotionTag>
    );
  }

  const shouldAnimate = isInView && isMeasured;

  const lineSlideVariants = {
    hidden: {
      transform: HIDDEN_TRANSFORM,
      opacity: 0,
    },
    show: {
      transform: VISIBLE_TRANSFORM,
      opacity: 1,
      transition: {
        transform: { duration: REVEAL_DURATION, ease: REVEAL_EASE },
        opacity: { duration: REVEAL_DURATION * 0.75, ease: REVEAL_EASE },
      },
    },
  };

  const wordColorVariants = {
    hidden: { color: colors.muted },
    show: {
      color: colors.primary,
      transition: { duration: REVEAL_DURATION, ease: REVEAL_EASE },
    },
  };

  const accentRevealVariants = accentWordColorVariants(colors.muted);

  return (
    <MotionTag
      ref={ref as never}
      role="region"
      aria-label={label}
      className={cn(
        "grid w-full max-w-full min-w-0 overflow-x-clip",
        className
      )}
      style={{ whiteSpace: "pre-wrap", color: colors.muted }}
    >
      {/*
        The copy is in the DOM exactly once: the hidden measure pass reserves
        layout until line tops are known, then the same text re-renders as
        animated lines. Crawlers and extractors read the heading once; the
        accessible name comes from aria-label on the container above.
      */}
      <span
        className="col-start-1 row-start-1 max-w-full min-w-0 overflow-x-clip"
        aria-hidden
      >
        {!isMeasured ? (
          <span className="break-words" style={{ visibility: "hidden" }}>
            {renderMeasurePieces(pieces, wordRefs)}
          </span>
        ) : (
          <motion.span
            className="block"
            initial={false}
            variants={lineContainerVariants}
            animate={shouldAnimate ? "show" : "hidden"}
            style={{ visibility: shouldAnimate ? "visible" : "hidden" }}
          >
            {lineGroups.map((linePieces, lineIndex) => (
              <span
                key={`line-${lineIndex}`}
                className="block overflow-hidden"
                style={{
                  paddingBottom: LINE_CLIP_DESCENDER_PADDING,
                  marginBottom: `calc(-1 * ${LINE_CLIP_DESCENDER_PADDING})`,
                }}
              >
                <motion.span
                  className="block break-words"
                  variants={lineSlideVariants}
                >
                  {renderAnimatedLinePieces(
                    linePieces,
                    `line-${lineIndex}`,
                    wordColorVariants,
                    accentRevealVariants.greyLayer,
                    accentRevealVariants.gradientLayer,
                    colors.muted
                  )}
                </motion.span>
              </span>
            ))}
          </motion.span>
        )}
      </span>
    </MotionTag>
  );
}
